import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import KpiCard from '../KpiCard.jsx';
import DateRangePicker, { rangeForDays } from '../DateRangePicker.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';

const DIMENSIONS = [
  { value: 'publisher', label: 'Publisher' },
  { value: 'country', label: 'Country' },
  { value: 'device', label: 'Device type' },
  { value: 'demand_partner', label: 'Demand partner (flagged-traffic incidence)' },
];

function pct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}

export default function TrafficQualityReportingTab() {
  const [range, setRange] = useState({ ...rangeForDays(30), days: 30 });
  const [publishers, setPublishers] = useState([]);
  const [publisherId, setPublisherId] = useState('');
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const [dimension, setDimension] = useState('publisher');
  const [breakdown, setBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(true);
  const [breakdownError, setBreakdownError] = useState(null);

  const [providerStats, setProviderStats] = useState(null);

  useEffect(() => {
    api.listPublishers().then(setPublishers).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingSummary(true);
    setSummaryError(null);
    api.trafficQualitySummary({ from: range.from, to: range.to, publisherId: publisherId || undefined })
      .then((res) => { if (!cancelled) setSummary(res); })
      .catch((err) => { if (!cancelled) setSummaryError(err instanceof ApiError ? err.message : 'Could not load summary.'); })
      .finally(() => { if (!cancelled) setLoadingSummary(false); });
    return () => { cancelled = true; };
  }, [range.from, range.to, publisherId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingBreakdown(true);
    setBreakdownError(null);
    api.trafficQualityBreakdown({ dimension, from: range.from, to: range.to })
      .then((res) => { if (!cancelled) setBreakdown(res); })
      .catch((err) => { if (!cancelled) setBreakdownError(err instanceof ApiError ? err.message : 'Could not load breakdown.'); })
      .finally(() => { if (!cancelled) setLoadingBreakdown(false); });
    return () => { cancelled = true; };
  }, [dimension, range.from, range.to]);

  useEffect(() => {
    let cancelled = false;
    api.trafficQualityProviderStats({ from: range.from, to: range.to })
      .then((res) => { if (!cancelled) setProviderStats(res); })
      .catch(() => { if (!cancelled) setProviderStats(null); });
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Summary</h2>
          <DateRangePicker from={range.from} to={range.to} activeDays={range.days} onChange={setRange} />
        </div>
        <label className="field" style={{ maxWidth: 320 }}>
          <span className="field__label">Publisher <span className="field__optional">(optional)</span></span>
          <select value={publisherId} onChange={(e) => setPublisherId(e.target.value)}>
            <option value="">All publishers</option>
            {publishers.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.domain})</option>)}
          </select>
        </label>

        {loadingSummary && <LoadingState label="Loading summary" />}
        {!loadingSummary && summaryError && <EmptyState title="Couldn't load summary" message={summaryError} />}
        {!loadingSummary && !summaryError && summary && (
          <>
            <div className="kpi-grid">
              <KpiCard label="Inbound requests" value={summary.totalRequests.toLocaleString()} tone="accent" />
              <KpiCard label="Clean" value={summary.cleanRequests.toLocaleString()} />
              <KpiCard label="Suspicious" value={summary.suspiciousRequests.toLocaleString()} />
              <KpiCard label="Blocked" value={summary.blockedRequests.toLocaleString()} />
              <KpiCard label="IVT rate" value={pct(summary.ivtRate)} tone="accent" />
            </div>
            {summary.note && <p className="card__hint" style={{ marginTop: '10px' }}>{summary.note}</p>}
          </>
        )}
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Breakdown</h2>
        </div>
        <div className="toolbar__filters" style={{ marginBottom: '16px' }}>
          {DIMENSIONS.map((d) => (
            <button key={d.value} className={`chip${dimension === d.value ? ' chip--active' : ''}`} onClick={() => setDimension(d.value)}>
              {d.label}
            </button>
          ))}
        </div>
        {loadingBreakdown && <LoadingState label="Loading breakdown" />}
        {!loadingBreakdown && breakdownError && <EmptyState title="Couldn't load breakdown" message={breakdownError} />}
        {!loadingBreakdown && !breakdownError && breakdown && breakdown.rows.length === 0 && (
          <EmptyState title="No data for this dimension" message="Try a wider date range once traffic starts flowing." />
        )}
        {!loadingBreakdown && !breakdownError && breakdown && breakdown.rows.length > 0 && (
          <>
            {breakdown.note && <p className="card__hint" style={{ marginBottom: '10px' }}>{breakdown.note}</p>}
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{DIMENSIONS.find((d) => d.value === dimension)?.label}</th>
                    <th className="num">Total</th>
                    <th className="num">Flagged</th>
                    <th className="num">Flagged rate</th>
                  </tr>
                </thead>
                <tbody>
                  {breakdown.rows.map((r) => (
                    <tr key={String(r.key)}>
                      <td className="cell-title">{String(r.key)}</td>
                      <td className="num">{r.total.toLocaleString()}</td>
                      <td className="num">{r.flagged.toLocaleString()}</td>
                      <td className="num">{pct(r.total > 0 ? r.flagged / r.total : null)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Provider performance</h2>
        </div>
        {providerStats?.note && <p className="card__hint">{providerStats.note}</p>}
        {providerStats && providerStats.rows.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider</th>
                  <th className="num">Evaluated</th>
                  <th className="num">Response rate</th>
                  <th className="num">Avg score</th>
                </tr>
              </thead>
              <tbody>
                {providerStats.rows.map((r) => (
                  <tr key={r.code}>
                    <td><code className="code-chip">{r.code}</code></td>
                    <td className="num">{r.evaluated.toLocaleString()}</td>
                    <td className="num">{pct(r.responseRate)}</td>
                    <td className="num">{r.avgScore !== null ? r.avgScore.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {providerStats && providerStats.rows.length === 0 && !providerStats.note && (
          <EmptyState title="No provider activity yet" />
        )}
      </section>

      <DiagnosticsLookup />
    </div>
  );
}

function DiagnosticsLookup() {
  const toast = useToast();
  const [auctionEventId, setAuctionEventId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function lookup(e) {
    e.preventDefault();
    if (!auctionEventId.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.trafficQualityDiagnostics(auctionEventId.trim()));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not load diagnostics.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">Diagnostics lookup</h2>
      </div>
      <p className="card__hint">
        Look up one real auction by its Auction Log event ID for a full explanation: why it was
        classified this way, what schain was sent, what authorization applied, which provider (if
        any) evaluated it, what policy decision followed, and whether the auction proceeded.
      </p>
      <form onSubmit={lookup} className="toolbar">
        <input
          className="toolbar__search"
          placeholder="Auction event ID…"
          value={auctionEventId}
          onChange={(e) => setAuctionEventId(e.target.value)}
          inputMode="numeric"
        />
        <button type="submit" className="btn btn--primary" disabled={loading}>{loading ? 'Looking up…' : 'Look up'}</button>
      </form>

      {error && <div className="form-error" style={{ marginTop: '12px' }}>{error}</div>}

      {result && (
        <div className="card" style={{ marginTop: '16px', background: 'var(--surface-sunken)' }}>
          <div className="card__header">
            <h3 className="card__title">Auction #{result.auctionEventId}</h3>
            <StatusBadge status={result.auctionProceeded ? 'active' : 'error'} />
          </div>
          <p className="card__hint">
            Publisher #{result.publisherId} &middot; {new Date(result.createdAt).toLocaleString()} &middot;{' '}
            {result.auctionProceeded ? 'Auction proceeded' : 'Blocked -- no demand call was made'}
          </p>

          <div className="kpi-grid" style={{ marginTop: '12px' }}>
            <KpiCard label="Classification" value={<StatusBadge status={result.classification || '—'} />} />
            <KpiCard label="Policy decision" value={<StatusBadge status={result.policyDecision || '—'} />} />
            <KpiCard label="Wins" value={result.winCount ?? 0} />
            <KpiCard label="Revenue (net INR)" value={result.revenueNetInr != null ? Number(result.revenueNetInr).toFixed(2) : '—'} />
          </div>

          <h3 className="placement-expansion__heading" style={{ marginTop: '18px' }}>IVT signals fired</h3>
          {result.ivtSignalsFired.length === 0 ? (
            <p className="card__hint">None.</p>
          ) : (
            <ul className="plain-list">
              {result.ivtSignalsFired.map((s, i) => (
                <li key={i}><code className="code-chip">{s.ruleCode}</code> ({s.severity}){s.reason ? ` — ${s.reason}` : ''}</li>
              ))}
            </ul>
          )}

          <h3 className="placement-expansion__heading" style={{ marginTop: '18px' }}>Provider</h3>
          <p className="card__hint">
            {result.provider ? `${result.provider.code} — score ${result.provider.score ?? '—'}` : 'No provider evaluated this auction.'}
          </p>

          <h3 className="placement-expansion__heading" style={{ marginTop: '18px' }}>Schain sent</h3>
          <p className="card__hint">
            Present: {result.schain.present ? 'Yes' : 'No'} &middot; Complete: {result.schain.complete ?? '—'} &middot; Nodes: {result.schain.nodeCount ?? '—'}
          </p>
          {result.schain.sent && <pre className="diff-view">{JSON.stringify(result.schain.sent, null, 2)}</pre>}

          <h3 className="placement-expansion__heading" style={{ marginTop: '18px' }}>Supply authorization</h3>
          <p className="card__hint">
            {result.supplyAuthorization.identityValue
              ? `${result.supplyAuthorization.identityType}: ${result.supplyAuthorization.identityValue} — status at auction time: ${result.supplyAuthorization.statusAtAuctionTime || '—'} (current: ${result.supplyAuthorization.currentStatus || '—'})`
              : 'No supply authorization identity recorded for this auction.'}
          </p>

          <h3 className="placement-expansion__heading" style={{ marginTop: '18px' }}>Demand partners</h3>
          {result.partnerResults.length === 0 ? (
            <p className="card__hint">No per-partner diagnostics recorded for this auction.</p>
          ) : (
            <table className="data-table" style={{ marginTop: '6px' }}>
              <thead>
                <tr><th>Partner</th><th>Outcome</th><th>Latency</th><th>HTTP status</th></tr>
              </thead>
              <tbody>
                {result.partnerResults.map((r) => (
                  <tr key={r.partnerCode}>
                    <td><code className="code-chip">{r.partnerCode}</code></td>
                    <td className="cell-muted">{r.outcome}</td>
                    <td className="cell-muted">{r.latencyMs != null ? `${r.latencyMs} ms` : '—'}</td>
                    <td className="cell-muted">{r.httpStatus ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
